import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Modal, Row, Col, Divider, Space, Button, Tooltip } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "@redux/rootReducer";
import { useAppDispatch } from "src/store";
import { fetchProductCategoryList } from "@redux/productSlice";
import timeUtils from "@utils/timeUtils";
import SCMContractApi, { CategoryRateDetail, SCMContract } from "@api/scmAPI";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { CONTRACT_MODE, CONTRACT_MODE_TEXT, ContractFieldType1, getBackChargeText } from "./utils";

const ModalContent = styled.div``;
const ReadOnlyText = styled.div`
  padding: 4px 11px;
  color: #0000009c;
`;

const FormItemCol = styled(Col)`
  .ant-form-item-label {
    padding: 0;
  }
  text-align: center;
`;

const CategoryRateWrapper = styled.div`
  background: #f0f7ff;
  border-radius: 4px;
  margin-top: 8px;
  margin-bottom: 12px;
  max-height: 300px;
  overflow-y: auto;
  position: relative;
`;
const CategoryRateHeader = styled.div`
  position: sticky;
  top: 0;
  background-color: #e6f2ff;
  padding: 12px;
  z-index: 1;
`;
const CategoryRateBody = styled.div`
  padding: 12px;
`;

type Props = {
  visible: boolean;
  contractId?: number | null;
  onClose: () => void;
};

export default function SCMContractViewModal(props: Props) {
  const { visible, contractId, onClose } = props;
  const dispatch = useAppDispatch();
  const { productCategory } = useSelector((state: RootState) => state.product);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<SCMContract | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!visible || !contractId) return;
      try {
        setLoading(true);
        dispatch(fetchProductCategoryList());
        const d: any = await SCMContractApi.fetchContractDetail(contractId);
        setDetail(d);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [visible, contractId, dispatch]);

  const level1Name = (id?: number) => (id ? productCategory.level1[id]?.name || "-" : "-");
  const level2Name = (id?: number) => (id ? productCategory.level2?.[id]?.name || "-" : "-");
  const rows: CategoryRateDetail[] = detail?.categoryRates || [];
  const modeNum = Number(detail?.contractMode || -999);
  const showCategoryRate = modeNum === CONTRACT_MODE.CONSIGNMENT || modeNum === CONTRACT_MODE.TRANS_SHIPMENT;

  return (
    <Modal open={visible} onCancel={onClose} width={700} footer={null} confirmLoading={loading}>
      <ModalContent>
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          <Row align="middle">
            <Col span={5}>最後更新者</Col>
            <Col span={19}>
              <ReadOnlyText>{detail?.lastUpdatedBy || "-"}</ReadOnlyText>
            </Col>
          </Row>
          <Row align="middle">
            <Col span={5}>廠商名稱</Col>
            <Col span={19}>
              <ReadOnlyText>{detail?.name || "-"}</ReadOnlyText>
            </Col>
          </Row>
          <Row align="middle">
            <Col span={5}>統編</Col>
            <Col span={19}>
              <ReadOnlyText>{detail?.ubn || "-"}</ReadOnlyText>
            </Col>
          </Row>
          <Row align="middle">
            <Col span={5}>合作類型</Col>
            <Col span={19}>
              <ReadOnlyText>{detail?.contractMode ? CONTRACT_MODE_TEXT[detail.contractMode] : "-"}</ReadOnlyText>
            </Col>
          </Row>
          <Row align="middle">
            <Col span={5}>生效日</Col>
            <Col span={19}>
              <ReadOnlyText>
                {detail?.startDate ? timeUtils.formatToTimestamp(detail.startDate, "YYYY-MM-DD") : "-"}
              </ReadOnlyText>
            </Col>
          </Row>
          <Row align="middle">
            <Col span={5}>結束日</Col>
            <Col span={19}>
              <ReadOnlyText>
                {detail?.endDate ? timeUtils.formatToTimestamp(detail.endDate, "YYYY-MM-DD") : "-"}
              </ReadOnlyText>
            </Col>
          </Row>
          <Row align="middle">
            <Col span={5}>後扣費用</Col>
            <Col span={19}>
              <ReadOnlyText>{getBackChargeText(detail?.contractMode)}</ReadOnlyText>
            </Col>
          </Row>
        </Space>
        <Divider />
        <Row gutter={16} align="middle" justify="space-around" style={{ marginBottom: 24 }}>
          {ContractFieldType1.map((field) => (
            <FormItemCol>
              <Space>
                {field.label}
                <Tooltip title={field.help}>
                  <ExclamationCircleOutlined style={{ color: "#1890ff", cursor: "help" }} />
                </Tooltip>
              </Space>
              <ReadOnlyText>{(detail as any)?.[field.name] ?? "-"}</ReadOnlyText>
            </FormItemCol>
          ))}
        </Row>
        {showCategoryRate ? (
          <CategoryRateWrapper>
            <CategoryRateHeader>
              <Row gutter={16} align="middle">
                <FormItemCol span={8}>第一品類</FormItemCol>
                <FormItemCol span={8}>第二品類</FormItemCol>
                <FormItemCol span={8}>抽成%</FormItemCol>
              </Row>
            </CategoryRateHeader>
            <CategoryRateBody>
              {rows.map((it, idx) => (
                <Row gutter={16} key={it.id ?? idx} style={{ marginBottom: 12 }} align="middle">
                  <FormItemCol span={8}>
                    <ReadOnlyText>{level1Name(it.categoryStId)}</ReadOnlyText>
                  </FormItemCol>
                  <FormItemCol span={8}>
                    <ReadOnlyText>{level2Name(it.categoryNdId)}</ReadOnlyText>
                  </FormItemCol>
                  <FormItemCol span={8}>
                    <ReadOnlyText>{it?.commissionRate ?? "-"}</ReadOnlyText>
                  </FormItemCol>
                </Row>
              ))}
            </CategoryRateBody>
          </CategoryRateWrapper>
        ) : null}
        <Row justify="end" align="middle">
          <Space>
            <Button onClick={onClose} loading={loading} type="primary">
              確定
            </Button>
          </Space>
        </Row>
      </ModalContent>
    </Modal>
  );
}
